import { Settings } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { ListItemIcon, Menu, MenuItem, SvgIconTypeMap } from "@mui/material";
import Fab from "@mui/material/Fab";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SelectToolbarProps<T> {
  onCancel: () => void;
  onDelete: () => void;
  processing: boolean;
  selected: T[];
  actions?: ActionProps<T>[];
}

const SelectToolbar = <T,>({
  onCancel,
  onDelete,
  processing,
  selected,
  actions,
}: SelectToolbarProps<T>) => {
  const { t } = useTranslation();

  const anchorEl = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);

  const numSelected = selected.length;

  return (
    <Toolbar sx={{ px: { xs: 3, sm: 6 } }}>
      <Tooltip title={"Clear selection"}>
        <Fab color="secondary" onClick={onCancel} variant="extended">
          <CloseIcon sx={{ mr: 1 }} />
          {numSelected} {t("common.selected")}
        </Fab>
      </Tooltip>
      {numSelected > 0 && (
        <div
          style={{
            flexDirection: "row",
            display: "flex",
            columnGap: "16px",
            alignItems: "center",
          }}
          ref={anchorEl}
        >
          <Tooltip title={"Actions"}>
            {/* A button, not an <a>: with no href it was not keyboard reachable.
                Styles are reset so it still renders as the bare icon it was. */}
            <button
              type="button"
              aria-label="Actions"
              style={{
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
                color: "inherit",
                font: "inherit",
                display: "inline-flex",
              }}
              onClick={(e) => {
                e.preventDefault();
                setOpen(!open);
              }}
            >
              <Settings />
            </button>
          </Tooltip>
          <ActionMenu
            open={open}
            anchorEl={anchorEl.current}
            actions={actions}
            onClose={() => setOpen(false)}
            row={selected[0]}
          />
          <Tooltip title={"Delete selected"}>
            <Fab color="secondary" disabled={processing} onClick={onDelete}>
              <DeleteIcon />
            </Fab>
          </Tooltip>
        </div>
      )}
    </Toolbar>
  );
};

export default SelectToolbar;

export interface ActionProps<T> {
  label: string;
  onClick: (row: T) => void;
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & {
    muiName: string;
  };
}

interface ActionMenuProps<T> {
  row: T;
  anchorEl: HTMLElement | null;
  actions?: ActionProps<T>[];
  open: boolean;
  onClose: () => void;
}

const ActionMenu = <T,>({
  open,
  anchorEl,
  actions,
  onClose,
  row,
}: ActionMenuProps<T>) => {
  return (
    <Menu
      id="action-menu"
      anchorEl={anchorEl}
      aria-labelledby="action-menu-button"
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {actions?.map((action, i) => (
        <MenuItem
          key={`action-${i}`}
          onClick={() => {
            action.onClick(row);
            onClose();
          }}
        >
          <ListItemIcon>
            <action.icon />
          </ListItemIcon>{" "}
          {action.label}
        </MenuItem>
      ))}
    </Menu>
  );
};
